/**
 * Booking Form - Create/Edit Bookings
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, hallAPI } from '../../services/api';
import api from '../../services/api';

const BookingForm = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(bookingId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [halls, setHalls] = useState([]);
  const [selectedHall, setSelectedHall] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);

  const [formData, setFormData] = useState({
    hall: '',
    floor: '', // For multi-floor halls like Uday Bhavan
    booking_date: '',
    function_type: 'celebration',
    function_details: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    guest_count: '',
    price: '',
    advance_paid: '',
    payment_mode: 'cash',
    special_requirements: '',
    internal_notes: '',
    status: 'confirmed',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchHalls();
    if (isEditMode) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  useEffect(() => {
    if (formData.hall) {
      const hall = halls.find(h => h.id === parseInt(formData.hall));
      setSelectedHall(hall);
      fetchBookedDates(formData.hall);

      // Set default price only if not in edit mode
      if (hall && !isEditMode) {
        // If hall has floors, wait for floor selection
        if (!hall.floors || hall.floors.length === 0) {
          setFormData(prev => ({ ...prev, price: hall.celebration_price }));
        }
      }
    }
  }, [formData.hall, halls]);

  // Update price when floor is selected
  useEffect(() => {
    if (formData.floor && selectedHall && selectedHall.floors) {
      const floor = selectedHall.floors.find(f => f.id === parseInt(formData.floor));
      if (floor && !isEditMode) {
        setFormData(prev => ({ ...prev, price: floor.price }));
      }
    }
  }, [formData.floor, selectedHall]);

  const fetchHalls = async () => {
    try {
      // Try to get admin's specific hall(s) first
      let hallsData = [];

      try {
        // Try admin-specific endpoint that returns only user's halls
        const adminResponse = await api.get('/admin/halls/');
        hallsData = Array.isArray(adminResponse.data) ? adminResponse.data : (adminResponse.data.results || []);
      } catch (err) {
        console.log('Admin halls endpoint not available, trying to get hall details');

        // Fallback: get the specific hall for this admin
        try {
          const hallResponse = await adminAPI.getHall();

          // If we get a single hall, put it in an array
          if (hallResponse.data) {
            hallsData = [hallResponse.data];

            // If this hall has floors, also include them as separate booking options
            if (hallResponse.data.floors && Array.isArray(hallResponse.data.floors)) {
              // For multi-floor halls, we'll keep just the main hall in dropdown
              // The floor selection will happen in the form
            }
          }
        } catch (hallErr) {
          console.log('Could not fetch admin hall, using all halls');
          // Last fallback: show all halls (for super admin)
          const response = await hallAPI.getAll();
          hallsData = Array.isArray(response.data) ? response.data : (response.data.results || []);
        }
      }

      setHalls(hallsData);
    } catch (error) {
      console.error('Error fetching halls:', error);
      setHalls([]);
    }
  };

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getBooking(bookingId);
      const booking = response.data;

      setFormData({
        hall: booking.hall,
        booking_date: booking.booking_date,
        function_type: booking.function_type,
        function_details: booking.function_details || '',
        customer_name: booking.customer_name,
        customer_phone: booking.customer_phone,
        customer_email: booking.customer_email || '',
        customer_address: booking.customer_address || '',
        guest_count: booking.guest_count || '',
        price: booking.price,
        advance_paid: booking.advance_paid,
        payment_mode: booking.payment_mode,
        special_requirements: booking.special_requirements || '',
        internal_notes: booking.internal_notes || '',
        status: booking.status,
      });
    } catch (error) {
      console.error('Error fetching booking:', error);
      alert('Failed to load booking details');
      navigate('/admin/bookings');
    } finally {
      setLoading(false);
    }
  };

  const [bookedFloorsByDate, setBookedFloorsByDate] = useState({});

  const fetchBookedDates = async (hallId) => {
    try {
      // Fetch all bookings for this hall to see which floors are booked on which dates
      const response = await adminAPI.getBookings();

      const hallBookings = (response.data.results || response.data).filter(
        b => b.hall === parseInt(hallId) && b.status !== 'cancelled'
      );

      // Create a map of date -> array of booked floor IDs
      const floorsByDate = {};
      hallBookings.forEach(booking => {
        const dateStr = booking.booking_date;
        if (!floorsByDate[dateStr]) {
          floorsByDate[dateStr] = [];
        }
        // If booking has a floor field (not null/undefined), add it to the list
        // This explicitly checks for null/undefined to handle old bookings without floor assignments
        if (booking.floor !== null && booking.floor !== undefined) {
          floorsByDate[dateStr].push(booking.floor);
        }
        // Note: Old bookings without floor field will simply not block any specific floor
        // This allows multi-floor halls to still book other floors even if old bookings exist
      });

      setBookedFloorsByDate(floorsByDate);

      // Keep the simple booked dates array for backward compatibility
      setBookedDates(Object.keys(floorsByDate));
    } catch (error) {
      console.error('Error fetching booked dates:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.hall) newErrors.hall = 'Please select a hall';

    // Check if floor is required (for multi-floor halls)
    if (selectedHall && selectedHall.floors && selectedHall.floors.length > 0) {
      if (!formData.floor) newErrors.floor = 'Please select a floor/venue';
    }

    if (!formData.booking_date) newErrors.booking_date = 'Please select a booking date';
    if (!formData.customer_name) newErrors.customer_name = 'Please enter customer name';
    if (!formData.customer_phone) newErrors.customer_phone = 'Please enter customer phone';
    if (formData.customer_phone && !/^\d{10}$/.test(formData.customer_phone.replace(/[\s-]/g, ''))) {
      newErrors.customer_phone = 'Please enter a valid 10-digit phone number';
    }
    if (formData.customer_email && !/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid email address';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    if (!formData.advance_paid) {
      newErrors.advance_paid = 'Please enter advance amount (or 0)';
    }
    if (parseFloat(formData.advance_paid) > parseFloat(formData.price)) {
      newErrors.advance_paid = 'Advance cannot be greater than total price';
    }

    // Check if date/floor combination is already booked (only for new bookings or when date changed)
    if (!isEditMode || formData.booking_date !== bookingId) {
      // For multi-floor halls, check if the specific floor is available
      if (selectedHall && selectedHall.floors && selectedHall.floors.length > 0) {
        // Multi-floor hall: check if selected floor is available
        if (formData.floor && !isFloorAvailable(parseInt(formData.floor))) {
          newErrors.floor = 'This floor is already booked on the selected date';
        }
      } else {
        // Single hall (no floors): check if date is booked
        if (bookedDates.includes(formData.booking_date)) {
          newErrors.booking_date = 'This date is already booked for the selected hall';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }

    try {
      setSaving(true);

      if (isEditMode) {
        // Update existing booking
        await adminAPI.updateBooking(bookingId, formData);
        alert('Booking updated successfully!');
      } else {
        // Create new booking
        await adminAPI.createBooking(formData);
        alert('Booking created successfully!');
      }

      navigate('/admin/bookings');
    } catch (error) {
      console.error('Error saving booking:', error);
      if (error.response?.data) {
        const apiErrors = error.response.data;
        if (typeof apiErrors === 'object') {
          setErrors(apiErrors);
        }
        alert(`Failed to save booking: ${JSON.stringify(apiErrors)}`);
      } else {
        alert('Failed to save booking. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const isDateBooked = (date) => {
    // For multi-floor halls, don't show "date is booked" message since we check per-floor
    if (selectedHall && selectedHall.floors && selectedHall.floors.length > 0) {
      return false; // Floor availability is checked separately
    }
    // For single halls without floors, check if the date is booked
    return bookedDates.includes(date) && (!isEditMode || date !== formData.booking_date);
  };

  const isFloorAvailable = (floorId) => {
    // If no date selected, all floors are available
    if (!formData.booking_date) return true;

    // If in edit mode and this is the current booking's floor and date, it's available
    if (isEditMode && formData.booking_date === bookingId) return true;

    // Check if this floor is booked on the selected date
    const bookedFloors = bookedFloorsByDate[formData.booking_date] || [];

    // If 'all' is in the array, entire hall is booked
    if (bookedFloors.includes('all')) return false;

    // Check if this specific floor is booked
    return !bookedFloors.includes(floorId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/bookings')}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Edit Booking' : 'New Booking'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? `Editing booking ${bookingId}` : 'Create a new hall booking'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Hall and Date Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hall Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hall <span className="text-red-500">*</span>
                </label>
                <select
                  name="hall"
                  value={formData.hall}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.hall ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select a hall</option>
                  {halls.map(hall => (
                    <option key={hall.id} value={hall.id}>
                      {hall.name}
                    </option>
                  ))}
                </select>
                {errors.hall && <p className="text-red-500 text-xs mt-1">{errors.hall}</p>}
              </div>

              {/* Floor Selection (only for multi-floor halls) */}
              {selectedHall && selectedHall.floors && selectedHall.floors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Floor/Venue <span className="text-red-500">*</span>
                    {formData.booking_date && (
                      <span className="text-xs text-gray-500 ml-2">(Grayed out = Already booked)</span>
                    )}
                  </label>
                  <select
                    name="floor"
                    value={formData.floor}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.floor ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">
                      {formData.booking_date ? 'Select an available floor/venue' : 'Select date first to see availability'}
                    </option>
                    {selectedHall.floors.map(floor => {
                      const available = isFloorAvailable(floor.id);
                      return (
                        <option
                          key={floor.id}
                          value={floor.id}
                          disabled={!available}
                          style={!available ? { color: '#9CA3AF', backgroundColor: '#F3F4F6' } : {}}
                        >
                          {floor.floor_name} - {floor.venue_name} (₹{floor.price}/day, Capacity: {floor.capacity})
                          {!available ? ' - BOOKED' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {errors.floor && <p className="text-red-500 text-xs mt-1">{errors.floor}</p>}
                  {formData.booking_date && bookedFloorsByDate[formData.booking_date] && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Some floors are already booked on this date
                    </p>
                  )}
                </div>
              )}

              {/* Booking Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="booking_date"
                  value={formData.booking_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.booking_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.booking_date && <p className="text-red-500 text-xs mt-1">{errors.booking_date}</p>}
                {formData.booking_date && isDateBooked(formData.booking_date) && (
                  <p className="text-red-500 text-xs mt-1">This date is already booked</p>
                )}
              </div>

              {/* Function Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Function Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="function_type"
                  value={formData.function_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="celebration">Celebration (Birthday, Wedding, Engagement, etc.)</option>
                  <option value="shradha">Shradha (Memorial Services)</option>
                </select>
              </div>

              {/* Guest Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Guest Count
                </label>
                <input
                  type="number"
                  name="guest_count"
                  value={formData.guest_count}
                  onChange={handleChange}
                  placeholder="Number of guests"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {selectedHall && (
                  <p className="text-xs text-gray-500 mt-1">
                    Hall capacity: {selectedHall.capacity} guests
                  </p>
                )}
              </div>

              {/* Function Details */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Function Details
                </label>
                <textarea
                  name="function_details"
                  value={formData.function_details}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Additional details about the function..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customer_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.customer_name && <p className="text-red-500 text-xs mt-1">{errors.customer_name}</p>}
              </div>

              {/* Customer Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  placeholder="10-digit phone number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customer_phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.customer_phone && <p className="text-red-500 text-xs mt-1">{errors.customer_phone}</p>}
              </div>

              {/* Customer Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  placeholder="customer@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customer_email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.customer_email && <p className="text-red-500 text-xs mt-1">{errors.customer_email}</p>}
              </div>

              {/* Customer Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Customer's address..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              </div>

              {/* Advance Paid */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Advance Paid <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="advance_paid"
                  value={formData.advance_paid}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.advance_paid ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.advance_paid && <p className="text-red-500 text-xs mt-1">{errors.advance_paid}</p>}
              </div>

              {/* Balance (auto-calculated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Balance Amount
                </label>
                <input
                  type="text"
                  value={`₹${(parseFloat(formData.price || 0) - parseFloat(formData.advance_paid || 0)).toFixed(2)}`}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Mode
                </label>
                <select
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
            <div className="space-y-6">
              {/* Booking Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Special Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Requirements
                </label>
                <textarea
                  name="special_requirements"
                  value={formData.special_requirements}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any special arrangements or requirements..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Internal Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Notes (Not visible to customer)
                </label>
                <textarea
                  name="internal_notes"
                  value={formData.internal_notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Internal notes for staff only..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/bookings')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Booking' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;
